import { useCallback, useEffect, useState } from "react";

import { TimeStamp } from "@/models/timestamp";
import { PlotPlanMapping, usePlotPlanMappingApi } from "@/sx-layout/hooks/usePlotPlanMappingApi";

type CreateParams = {
  detail_plot_plan_id: number;
  clip_x: number;
  clip_y: number;
  clip_w: number;
  clip_h: number;
  uploadfile: File | Blob;
  scale_x: number;
  scale_y: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
};

type UpdateParams = {
  clip_x: number;
  clip_y: number;
  clip_w: number;
  clip_h: number;
  uploadfile?: File | Blob;
  scale_x: number;
  scale_y: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  timestamp: TimeStamp;
};

export const usePlotPlanMapping = (overviewPlotPlanId: number, errorCallback?: () => void) => {
  const api = usePlotPlanMappingApi();
  // TODO: globalなstateにするか
  const [plotPlanDetails, setPlotPlanDetails] = useState<PlotPlanMapping[]>([]);

  const refresh = useCallback(async () => {
    if (!overviewPlotPlanId) {
      setPlotPlanDetails([]);
      return;
    }
    const res = await api.fetchPlotPlanMappings({ overview_plot_plan_id: overviewPlotPlanId });
    setPlotPlanDetails(res?.plot_plan_mappings ?? []);
  }, [overviewPlotPlanId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * 新規作成（uploadfile 必須）。
   */
  const createDetail = useCallback(
    async (params: CreateParams) => {
      await api.createPlotPlanMapping({
        overview_plot_plan_id: overviewPlotPlanId,
        ...params,
      });
      await refresh();
    },
    [api, overviewPlotPlanId, refresh]
  );

  const updatePartial = useCallback(
    (params: {
      plotPlanMappingId: number;
      payload: Partial<PlotPlanMapping>;
      plotPlanMappings?: PlotPlanMapping[];
    }) => {
      const { plotPlanMappingId, payload, plotPlanMappings } = params;
      const list = (plotPlanMappings ?? plotPlanDetails).concat();
      const index = list.findIndex((v) => v.plot_plan_mapping_id === plotPlanMappingId);
      list.splice(index, 1, { ...list[index], ...payload });
      setPlotPlanDetails(list);
      return list;
    },
    [plotPlanDetails]
  );

  const reloadCallback = useCallback(
    (_error: any) => {
      errorCallback?.();
      refresh();
    },
    [errorCallback, refresh]
  );

  /**
   * 全項目更新（クリップ画像の変更を含む場合は uploadfile を渡す）。
   */
  const updateDetail = useCallback(
    async (plotPlanMappingId: number, params: UpdateParams) => {
      const list = updatePartial({ plotPlanMappingId, payload: params });
      const response = await api.updatePlotPlanMapping({ plotPlanMappingId, payload: params, reloadCallback });
      updatePartial({ plotPlanMappingId, payload: { timestamp: response.timestamp }, plotPlanMappings: list });
    },
    [api, reloadCallback, updatePartial]
  );

  /**
   * 座標（x, y）更新（移動確定時）。
   */
  const updateCoord = useCallback(
    async (plotPlanMappingId: number, coord: { x: number; y: number }, timestamp: TimeStamp) => {
      const list = updatePartial({ plotPlanMappingId, payload: coord });
      const response = await api.updatePlotPlanMappingCoord({
        plotPlanMappingId,
        payload: { ...coord, timestamp },
        reloadCallback,
      });
      updatePartial({ plotPlanMappingId, payload: { timestamp: response.timestamp }, plotPlanMappings: list });
    },
    [api, reloadCallback, updatePartial]
  );

  /**
   * 回転角度更新（回転確定時）。
   */
  const updateRotation = useCallback(
    async (plotPlanMappingId: number, rotation: number, timestamp: TimeStamp) => {
      const rounded = Math.round(rotation);
      const list = updatePartial({ plotPlanMappingId, payload: { rotation: rounded } });
      const response = await api.updatePlotPlanMappingRotation({
        plotPlanMappingId,
        payload: {
          rotation: rounded,
          timestamp,
        },
        reloadCallback,
      });
      updatePartial({ plotPlanMappingId, payload: { timestamp: response.timestamp }, plotPlanMappings: list });
    },
    [api, reloadCallback, updatePartial]
  );

  /**
   * 表示スケール（scale_x, scale_y）と座標（x, y）更新（リサイズ確定時）。
   *
   * 左方向・上方向へのリサイズや回転を伴う場合、表示矩形の左上座標（x, y）が変化するため、
   * scale だけでなく座標も一緒に更新する。
   */
  const updateScale = useCallback(
    async (
      plotPlanMappingId: number,
      coord: { x: number; y: number },
      scale: { scale_x: number; scale_y: number },
      timestamp: TimeStamp
    ) => {
      const list = updatePartial({ plotPlanMappingId, payload: { ...coord, ...scale } });
      const coordRes = await api.updatePlotPlanMappingCoord({
        plotPlanMappingId,
        payload: { ...coord, timestamp },
        reloadCallback,
      });
      const response = await api.updatePlotPlanMappingScale({
        plotPlanMappingId,
        payload: {
          ...scale,
          timestamp: coordRes.timestamp,
        },
        reloadCallback,
      });
      updatePartial({ plotPlanMappingId, payload: { timestamp: response.timestamp }, plotPlanMappings: list });
    },
    [api, reloadCallback, updatePartial]
  );

  /**
   * 削除。
   */
  const remove = useCallback(
    async (plotPlanMappingId: number) => {
      await api.deletePlotPlanMapping(plotPlanMappingId);
      await refresh();
    },
    [api, refresh]
  );

  return {
    plotPlanDetails,
    refresh,
    createDetail,
    updateDetail,
    updateCoord,
    updateRotation,
    updateScale,
    remove,
  };
};
